/**
 * Merge-jar chrome: the cartoon glass jar (render/toon/jar/jar-art.js) around the creatures.
 * The back layer (glass tint, shade, bubbles) sits behind the bodies; the front layer (walls,
 * ink, highlight bands) and the mouth's front rim sit in front of them. All are baked canvases
 * rebuilt only when the jar size changes. Shared by Pixi (pooled texture sprites) and Canvas 2D
 * (drawImage). All follow the jar wobble: callers pass the shaken origin.
 *
 * The aimed creature waiting at the mouth is page chrome above the canvas, so the rim can never
 * pass in front of it; instead, while a drop is aimed, the rim fades out around the aim (drawn
 * in vertical slices of its strip, each with its own alpha), so it never runs behind it.
 */
import { INK } from "./toon/palette.js";
import { getJarLayers } from "./toon/jar/jar-art.js";
import { clampJarURadius, traceJarUPath } from "./toon/jar/jar-path.js";

export { clampJarURadius, traceJarUPath };

/** World z of the jar layers: back under everything, front over bodies (20) but under FX. */
export var JAR_BACK_Z = 0;
export var JAR_FRONT_Z = 18;
export var JAR_RIM_Z = 19;

/** Slices per side of the rim's fade ramp. */
var RIM_RAMP_SLICES = 16;

function smoothstep(t) {
  var k = t < 0 ? 0 : t > 1 ? 1 : t;
  return k * k * (3 - 2 * k);
}

/**
 * The rim strip as vertical slices [x0, x1, alpha] in jar-local px (x from the inner left
 * wall), covering the whole strip. Without a fade it is one opaque slice. With one, it is clear
 * (alpha 1 - amount) within `clear` px of `x`, ramping smoothly back to opaque over `ramp` px.
 * Slice edges land on whole pixels of the baked strip, so neighbours meet without seams.
 * @param {{ rimLeft: number, rimWidth: number, rim?: { width: number } }} art
 * @param {{ x: number, clear: number, ramp: number, amount: number }|null} fade
 * @returns {Array<[number, number, number]>}
 */
export function jarRimSlices(art, fade) {
  var L = art.rimLeft, R = art.rimLeft + art.rimWidth;
  var res = art.rim && art.rim.width > 0 ? art.rim.width / art.rimWidth : 1;
  var snap = function (v) { return L + Math.round((v - L) * res) / res; };
  var amount = fade && isFinite(fade.amount) ? Math.max(0, Math.min(1, fade.amount)) : 0;
  if (amount <= 0.005 || !isFinite(fade.x)) return [[L, R, 1]];
  var clear = Math.max(0, fade.clear || 0), ramp = Math.max(1, fade.ramp || 1);
  var edges = [L, fade.x - clear - ramp];
  var i;
  for (i = 1; i <= RIM_RAMP_SLICES; i++) edges.push(fade.x - clear - ramp + (ramp * i) / RIM_RAMP_SLICES);
  edges.push(fade.x + clear);
  for (i = 1; i <= RIM_RAMP_SLICES; i++) edges.push(fade.x + clear + (ramp * i) / RIM_RAMP_SLICES);
  edges.push(R);
  var out = [];
  var last = L;
  for (i = 1; i < edges.length; i++) {
    var x1 = snap(Math.max(L, Math.min(R, edges[i])));
    if (x1 <= last) continue;
    var mid = (last + x1) / 2, d = Math.abs(mid - fade.x);
    var k = d <= clear ? 0 : smoothstep((d - clear) / ramp);
    out.push([last, x1, 1 - amount * (1 - k)]);
    last = x1;
  }
  if (last < R) out.push([last, R, 1]);
  return out;
}

/**
 * Board-space box of the jar's inner U (the physics silhouette).
 * @param {{ neckLeft: number, neckRight: number, T: number, bodyBottom: number }} geom
 * @param {{ x?: number, y?: number }} origin flask panel origin (with the jar wobble)
 * @param {number} cornerRadius
 * @param {{ panelHeight?: number, resolution?: number }} [opts] panel height lets the bottom wall
 *   fit above the clip; resolution is the renderer density the glass is baked at
 */
export function layoutShakeJarChrome(geom, origin, cornerRadius, opts) {
  if (!geom) return null;
  var o = opts || {};
  var ox = origin && origin.x != null ? origin.x : 0;
  var oy = origin && origin.y != null ? origin.y : 0;
  var innerW = Math.max(24, geom.neckRight - geom.neckLeft);
  var innerH = Math.max(24, geom.bodyBottom - geom.T);
  var jarR = clampJarURadius(cornerRadius, innerW, innerH);
  var x = ox + geom.neckLeft;
  var y = oy + geom.T;
  return {
    x: x,
    y: y,
    width: innerW,
    height: innerH,
    radius: jarR,
    innerX: x + 3,
    innerY: y,
    innerWidth: Math.max(1, innerW - 6),
    innerHeight: Math.max(1, innerH - 3),
    innerRadius: Math.max(2, jarR - 2),
    roomBelow: o.panelHeight > 0 ? o.panelHeight - geom.bodyBottom : null,
    resolution: o.resolution > 0 ? o.resolution : 1,
  };
}

/** The baked glass for a layout (null without a 2D canvas). */
export function getShakeJarArt(layout) {
  if (!layout) return null;
  return getJarLayers(layout.width, layout.height, layout.radius, {
    roomBelow: layout.roomBelow,
    resolution: layout.resolution,
  });
}

/**
 * Publish the jar: back glass under the sprites, front glass and rim over them. No-op without a
 * playfield.
 * @param {object} playfield
 * @param {ReturnType<typeof layoutShakeJarChrome>} layout
 * @param {{ x: number, clear: number, ramp: number, amount: number }|null} [rimFade] fade the rim
 *   out around jar-local `x` (see jarRimSlices)
 * @returns {boolean} true when GPU chrome was published
 */
export function syncShakeJarChrome(playfield, layout, rimFade) {
  if (!playfield || !layout) return false;
  if (typeof playfield.underFx !== "function" || typeof playfield.effect !== "function") {
    return false;
  }
  var art = getShakeJarArt(layout);
  if (art) {
    var box = {
      kind: "texture",
      x: layout.x + art.left,
      y: layout.y + art.top,
      width: art.width,
      height: art.height,
      anchorX: 0,
      anchorY: 0,
    };
    playfield.underFx(Object.assign({ key: "jar-back", image: art.back, zIndex: JAR_BACK_Z }, box));
    playfield.effect(Object.assign({ key: "jar-front", image: art.front, zIndex: JAR_FRONT_Z }, box));
    var res = art.rim.width / art.rimWidth;
    jarRimSlices(art, rimFade).forEach(function (s, i) {
      playfield.effect({
        key: "jar-rim-" + i,
        kind: "texture",
        image: art.rim,
        frame: [(s[0] - art.rimLeft) * res, 0, (s[1] - s[0]) * res, art.rim.height],
        x: layout.x + s[0],
        y: layout.y + art.rimTop,
        width: s[1] - s[0],
        height: art.rimHeight,
        anchorX: 0,
        anchorY: 0,
        alpha: s[2],
        zIndex: JAR_RIM_Z,
      });
    });
    return true;
  }
  // No 2D canvas to bake into: a flat glass fill and one ink line on the same U.
  var u = {
    kind: "jar-u",
    x: layout.x,
    y: layout.y,
    width: layout.width,
    height: layout.height,
    radius: layout.radius,
  };
  playfield.underFx(Object.assign({}, u, {
    key: "jar-fill",
    closeTop: true,
    fill: 0x96d4f6,
    fillAlpha: 0.13,
    zIndex: JAR_BACK_Z,
  }));
  playfield.effect(Object.assign({}, u, {
    key: "jar-ink",
    stroke: 3,
    color: INK,
    strokeAlpha: 1,
    zIndex: JAR_FRONT_Z,
  }));
  return true;
}

var ambient = { last: null, next: 0 };

/**
 * Ambient life in the glass: now and then a small bubble rises from the floor behind the
 * creatures (juice engine back layer, so it is pause-aware, capped and off with particles off).
 * @param {{ engine?: { sparkles: Function } }|null} juice game.juice
 * @param {ReturnType<typeof layoutShakeJarChrome>} layout jar box in canvas px (shaken origin)
 * @param {number} t pause-aware seconds
 */
export function tickJarAmbient(juice, layout, t) {
  if (!juice || !juice.engine || typeof juice.engine.sparkles !== "function" || !layout) return;
  if (!isFinite(t)) return;
  if (ambient.last == null || t < ambient.last || t - ambient.last > 5) {
    ambient.last = t;
    ambient.next = t + 0.6;
    return;
  }
  ambient.last = t;
  if (t < ambient.next) return;
  ambient.next = t + 0.7 + Math.random() * 1.1;
  var unit = Math.max(0.7, Math.min(1.6, layout.width / 420));
  var x = layout.x + layout.radius + Math.random() * Math.max(1, layout.width - layout.radius * 2);
  juice.engine.sparkles(x, layout.y + layout.height - 3, {
    count: 1,
    shape: "bubble",
    colors: ["#dff4ff"],
    speed: [2, 8],
    up: 26 * unit,
    gravity: -30 * unit,
    drag: 0.5,
    angle: -Math.PI / 2,
    spread: 0.5,
    spin: 0,
    size: [3 * unit, 5.5 * unit],
    life: [1.5, 2.4],
    layer: "back",
  });
}

/**
 * Canvas 2D: draw one jar layer. `layout` is in the context's current space (the caller has
 * translated to the shaken panel origin).
 * @param {CanvasRenderingContext2D} ctx
 * @param {ReturnType<typeof layoutShakeJarChrome>} layout
 * @param {"back"|"front"} which "front" also draws the rim
 * @param {{ x: number, clear: number, ramp: number, amount: number }|null} [rimFade] see jarRimSlices
 * @returns {boolean}
 */
export function drawShakeJarLayer(ctx, layout, which, rimFade) {
  if (!ctx || !layout) return false;
  var art = getShakeJarArt(layout);
  if (art && typeof ctx.drawImage === "function") {
    var img = which === "front" ? art.front : art.back;
    ctx.drawImage(img, 0, 0, img.width, img.height, layout.x + art.left, layout.y + art.top, art.width, art.height);
    if (which === "front") {
      var res = art.rim.width / art.rimWidth;
      var alpha0 = ctx.globalAlpha;
      jarRimSlices(art, rimFade).forEach(function (s) {
        if (s[2] <= 0.001) return;
        ctx.globalAlpha = alpha0 * s[2];
        ctx.drawImage(art.rim, (s[0] - art.rimLeft) * res, 0, (s[1] - s[0]) * res, art.rim.height,
          layout.x + s[0], layout.y + art.rimTop, s[1] - s[0], art.rimHeight);
      });
      ctx.globalAlpha = alpha0;
    }
    return true;
  }
  if (typeof ctx.beginPath !== "function") return false;
  ctx.save();
  ctx.beginPath();
  traceJarUPath(ctx, layout.x, layout.y, layout.width, layout.height, layout.radius, which !== "front");
  if (which === "front") {
    ctx.lineJoin = "round";
    ctx.lineCap = "round";
    ctx.lineWidth = 3;
    ctx.strokeStyle = INK;
    ctx.stroke();
  } else {
    ctx.fillStyle = "rgba(150, 212, 246, 0.13)";
    ctx.fill();
  }
  ctx.restore();
  return true;
}
